import {
  Body1,
  Card,
  Subtitle1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { CoverageMatrix } from '@lg/core';
import type { DocumentType } from '@lg/shared';
import { DOC_LABEL, usd } from '../ui/format';

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  wrap: { overflowX: 'auto' },
  cell: {
    textAlign: 'center',
    cursor: 'pointer',
    minWidth: '64px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    border: `2px solid ${tokens.colorNeutralBackground1}`,
  },
  ok: { backgroundColor: tokens.colorPaletteGreenBackground2, color: tokens.colorPaletteGreenForeground2 },
  warn: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
  gap: { backgroundColor: tokens.colorPaletteRedBackground2, color: tokens.colorPaletteRedForeground2 },
  legend: { display: 'flex', gap: tokens.spacingHorizontalL, flexWrap: 'wrap' },
  head: { fontSize: tokens.fontSizeBase200, whiteSpace: 'nowrap' },
});

/** Document type × amount band coverage (spec 10.4). Click a cell to preview that case. */
export function CoverageMatrixView({
  coverage,
  onPick,
}: {
  coverage: CoverageMatrix;
  onPick: (documentType: DocumentType, amountUsd: number, submitterUpn: string | null) => void;
}) {
  const styles = useStyles();
  return (
    <Card className={styles.card}>
      <Subtitle1 as="h2">Approval routing coverage</Subtitle1>
      <Body1>
        Every worker with a position is tested as the submitter at $1, at each approval limit and one dollar
        above it.
        {coverage.gapCount === 0
          ? ' Every case reaches an approver.'
          : ` ${coverage.gapCount} band${coverage.gapCount > 1 ? 's have' : ' has'} no approver.`}{' '}
        Click a cell to preview the chain.
      </Body1>
      <div className={styles.wrap}>
        <Table size="extra-small" aria-label="Coverage matrix">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Document</TableHeaderCell>
              {coverage.amounts.map((a) => (
                <TableHeaderCell key={a} className={styles.head}>
                  {usd(a)}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {coverage.rows.map((row) => (
              <TableRow key={row.documentType}>
                <TableCell>
                  {DOC_LABEL[row.documentType]}
                  {!row.inScope && ' (out of scope)'}
                </TableCell>
                {row.cells.map((c) => (
                  <Tooltip
                    key={c.amountUsd}
                    relationship="description"
                    content={
                      c.example?.message ??
                      `${DOC_LABEL[row.documentType]} of ${usd(c.amountUsd)}: approver found`
                    }
                  >
                    <TableCell
                      className={mergeClasses(
                        styles.cell,
                        c.status === 'ok' ? styles.ok : c.status === 'gap' ? styles.gap : styles.warn,
                      )}
                      onClick={() => onPick(row.documentType, c.amountUsd, c.example?.submitterUpn ?? null)}
                    >
                      {c.status === 'ok' ? 'ok' : c.status === 'gap' ? 'gap' : '!'}
                    </TableCell>
                  </Tooltip>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className={styles.legend}>
        <span>
          <span className={mergeClasses(styles.cell, styles.ok)} style={{ padding: '0 6px' }}>
            ok
          </span>{' '}
          approver found
        </span>
        <span>
          <span className={mergeClasses(styles.cell, styles.warn)} style={{ padding: '0 6px' }}>
            !
          </span>{' '}
          vacant position skipped
        </span>
        <span>
          <span className={mergeClasses(styles.cell, styles.gap)} style={{ padding: '0 6px' }}>
            gap
          </span>{' '}
          nobody can approve
        </span>
      </div>
    </Card>
  );
}
